import React from "react";
import type { NewsletterDTO } from "@/generated-client";
import { Localizer } from "@/main/react/Types";
import {
    NewsletterGetByEmailLine,
    useMemberPublicNewsletters,
} from "@/main/react/newsletter/NewsletterGetByEmailLine";
import { newsletterPageUrl } from "@/main/react/newsletter/newsletterUrls";

/**
 * D13 (newsletter growth work order, slice 2c): the member's public newsletters as cards in the profile
 * header's newsletter slot, one per ACTIVE + PUBLIC newsletter (a member holds at most five, A13).
 *
 * <p>Renders NOTHING (no wrapper, no heading) while the read is unanswered, when it fails, and when the member
 * has no public newsletter, so `UserHeader`'s `empty:hidden` block closes up exactly as it does for a profile
 * with no showcase. It lives INSIDE the `UserPage` island through a slot, never as a `react-component` of its
 * own: `user.jte` is the template of the 2026-07-28 nested-island defect (rule 32).
 *
 * <p>The subscriber count shows only when the API returns one (A16: the server withholds it from non-owners
 * below the public threshold). The owner sees the card without the subscribe line, since a member cannot
 * subscribe to their own newsletter.
 */
export const PROFILE_NEWSLETTER_CARD_LIMIT = 5;

export type ProfileNewsletterCardProps = {
    newsletter: NewsletterDTO;
    localizer: Localizer;
    viewerIsOwner: boolean;
    loggedIn: boolean;
};

export function ProfileNewsletterCard({ newsletter, localizer, viewerIsOwner, loggedIn }: ProfileNewsletterCardProps) {
    const pageUrl = newsletterPageUrl(newsletter.slug);
    const blurb = newsletter.promiseLine || newsletter.description;
    const count = typeof newsletter.subscribersCount === "number" ? newsletter.subscribersCount : null;
    return (
        <section
            className="flex w-full flex-col gap-3 rounded-xl border border-border bg-card p-4"
            data-playwright-id="profile-newsletter-card"
            data-newsletter-public-id={newsletter.publicId}
        >
            <div className="flex min-w-0 flex-col gap-1">
                {pageUrl ? (
                    <a className="standard-link truncate text-base font-semibold text-foreground" href={pageUrl}>
                        {newsletter.name}
                    </a>
                ) : (
                    <span className="truncate text-base font-semibold text-foreground">{newsletter.name}</span>
                )}
                {blurb ? <p className="line-clamp-2 text-sm text-muted-foreground">{blurb}</p> : null}
                {count !== null ? (
                    <p className="text-xs text-muted-foreground" data-playwright-id="profile-newsletter-card-count">
                        {localizer("newsletter.subscribers", String(count))}
                    </p>
                ) : null}
            </div>
            {viewerIsOwner || newsletter.viewerOwner ? null : (
                <NewsletterGetByEmailLine
                    newsletter={newsletter}
                    localizer={localizer}
                    loggedIn={loggedIn}
                    surface="profile"
                />
            )}
        </section>
    );
}

export type ProfileNewsletterCardsProps = {
    username: string;
    localizer: Localizer;
    viewerIsOwner: boolean;
    loggedIn: boolean;
};

/** The slot's content: fetches `by-user/{username}` once and renders one card per newsletter, or nothing. */
export function ProfileNewsletterCards({ username, localizer, viewerIsOwner, loggedIn }: ProfileNewsletterCardsProps) {
    const newsletters = useMemberPublicNewsletters(username);
    if (!newsletters || newsletters.length === 0) {
        return null;
    }
    return (
        <>
            {newsletters.slice(0, PROFILE_NEWSLETTER_CARD_LIMIT).map((newsletter) => (
                <ProfileNewsletterCard
                    key={newsletter.publicId}
                    newsletter={newsletter}
                    localizer={localizer}
                    viewerIsOwner={viewerIsOwner}
                    loggedIn={loggedIn}
                />
            ))}
        </>
    );
}

export default ProfileNewsletterCards;
